import { BaseApiClient, IApiClientOptions, IRequestProps } from 'core';
import { isAPIError, isSocketHangUpError, MAX_RE_FETCHES } from '../utils/api-utils';
import { SocketHangUpError } from '../errors/socket-hang-up';

export interface IResponseOptions {
    status: 'error' | 'success';
    data: any;
    details?: any;
}

export enum HTTP_METHOD {
    GET = 'get',
    POST = 'post',
    PUT = 'put',
    DELETE = 'delete',
    PATCH = 'patch',
}

export abstract class AssureBaseApiClient extends BaseApiClient {
    constructor(clientName: string, options: IApiClientOptions) {
        super(clientName, options);
    }

    public async deleteEntity<T>(params?: any): Promise<T> {
        return this.send<T>(HTTP_METHOD.DELETE, this.getRequestOptions('delete', params));
    }

    protected abstract getRootPath(): string;

    protected getRequestOptions(subPathName?: string, params?: any): IRequestProps {
        return {
            url: this._getRequestPath(subPathName),
            params,
            options: {
                propagateError: true,
            },
        };
    }

    protected _convertToValidQuery(options: any): any {
        if (!options) return {};
        return Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            Object.entries(options)
                .filter(([key, value]) => value !== undefined && value !== null)
                .filter(([key, value]) => {
                    if (typeof value === 'string' && value.trim() === '') {
                        return false;
                    }
                    if (Array.isArray(value) && value.length === 0) {
                        return false;
                    }
                    return true;
                }).map(([key, value]) => {
                    if (Array.isArray(value)) {
                        return [key, value.join(',')];
                    }
                    return [key, value];
                })
        );
    }

    protected getRequestOptionsWithAccountId(accountId: number, subPathName?: string, params?: any, query?: string): IRequestProps {
        return {
            url: `/account/${accountId}/${this._getRequestPath(subPathName, query)}`,
            params: params,
            options: {
                propagateError: true,
            },
        };
    }

    protected send<T>(httpMethod: HTTP_METHOD, requestProps: IRequestProps): Promise<T> {
        return this._responseResult(this[httpMethod], requestProps);
    }

    protected async getAllEntities<T>(params?: any): Promise<T> {
        return this.send<T>(HTTP_METHOD.GET, this.getRequestOptions('', params));
    }

    private _getRequestPath(subPathName?: string, query?: string): string {
        const rootPath = this.getRootPath();
        return `${rootPath}${subPathName && subPathName.length > 0 ? `/${subPathName}` : ''}${query && query.length > 0 ? `?${query}` : ''}`;
    }

    private async _responseResult<T>(
        httpMethodFn: (request: IRequestProps) => Promise<IResponseOptions>,
        request: IRequestProps,
        reFetches = 0,
    ): Promise<T> {
        const response = await httpMethodFn(request);
        if (response.status === 'error') {
            if (isSocketHangUpError(response)) {
                if (reFetches < MAX_RE_FETCHES) {
                    return this._responseResult(httpMethodFn, request, reFetches + 1);
                }
                throw new SocketHangUpError(response.data);
            }
            if (isAPIError(response)) {
                throw Error(response.details?.details?.error || response.data);
            }
            throw Error(response.data);
        }
        return response.data;
    }
}
